import React, { useState } from "react";
import { Book } from "../types/Book";
import TrailerPlayer from "./TrailerPlayer";
import OptimizedImage from "./OptimizedImage";
import analytics from "../services/analytics";

interface BookCardProps {
  book: Book;
  onSelect: (bookId: string) => void;
}

const BookCard: React.FC<BookCardProps> = ({ book, onSelect }) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [audioState, setAudioState] = useState<{isPlaying: boolean, hasStopped: boolean, hasStartedPlaying: boolean}>({
    isPlaying: false,
    hasStopped: false,
    hasStartedPlaying: false
  });

  const handleCoverClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(book.id);
  };

  const toggleDescription = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newExpandedState = !isDescriptionExpanded;
    setIsDescriptionExpanded(newExpandedState);

    // Track description expansion
    analytics.trackDescriptionExpanded(
      book.id,
      newExpandedState,
      book.logline?.length || 0
    );
  };

  return (
    <div className="book-card">
      <div className={`book-cover ${audioState.isPlaying ? 'playing' : ''}`} onClick={handleCoverClick}>
        <OptimizedImage
          src={book.cover_image || ''}
          alt={`${book.title} cover`}
          lazy={true}
          className="book-cover-image"
        />
        {book.preview_mode && <div className="preview-ribbon">Preview</div>}
      </div>
      <div className="book-info">
        <h3 className="book-title">{book.title}</h3>
        {book.author && book.author_website && (
          <p className="book-author">
            by{" "}
            <a
              href={`https://${book.author_website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="author-link"
            >
              {book.author}
            </a>
          </p>
        )}
        <p className="book-genre">{book.genre}</p>
        <ul className="book-details">
          <li>{book.estimated_duration}</li>
          <li>{book.target_age}</li>
        </ul>
        <div className="book-description-container">
          <p
            className={`book-description ${
              isDescriptionExpanded ? "expanded" : "collapsed"
            }`}
          >
            {book.logline}
          </p>
          {book.logline && book.logline.length > 100 && (
            <button
              className="description-toggle"
              onClick={toggleDescription}
              title={isDescriptionExpanded ? "Show less" : "Show more"}
            >
              {isDescriptionExpanded ? "Show less" : "Show more"}
            </button>
          )}
        </div>

        <TrailerPlayer
          bookId={book.id}
          audioFile={book.audio_trailer}
          bookTitle={book.title}
          onAudioStateChange={setAudioState}
        />
      </div>
    </div>
  );
};

export default BookCard;
